import { navigateTo } from '../router';
import { apiRequest } from '../api';
import { getLocale } from '../i18n';

// ============================================================
// Страница «Поддержать проект».
// Текст и способы поддержки статичны в index.html (i18n через data-i18n),
// здесь логика: кнопка «Назад», копирование номера карты и список донатов
// (полученных переводов), который ведёт админ.
// ============================================================

/** Номер карты Сбер для поддержки (без пробелов). */
const CARD_NUMBER = '2202208167331108';

const CURRENCY_SYMBOLS: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€' };

interface Donation {
    id: number;
    donor_name: string;
    amount: number;
    currency: string;
    message: string | null;
    donated_at: string;
}

export function mountSupport(): void {
    document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
        el.classList.toggle('hidden', el.id !== 'screen-support');
    });

    const backBtn = document.getElementById('btn-back-support');
    if (backBtn) {
        const fresh = backBtn.cloneNode(true) as HTMLElement;
        backBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => {
            // Возврат на предыдущую страницу, если она есть в истории SPA
            if (window.history.length > 1) {
                window.history.back();
            } else {
                navigateTo('/', true);
            }
        });
    }

    const copyBtn = document.getElementById('support-copy-card');
    if (copyBtn) {
        const fresh = copyBtn.cloneNode(true) as HTMLElement;
        copyBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => {
            void (async () => {
                try {
                    await navigator.clipboard.writeText(CARD_NUMBER);
                    const original = fresh.textContent;
                    fresh.textContent = '✓ ' + original;
                    setTimeout(() => {
                        fresh.textContent = original;
                    }, 1500);
                } catch {
                    /* clipboard недоступен — номер и так виден на странице */
                }
            })();
        });
    }

    void loadDonations();
}

/** Загружает список донатов и рисует его внизу страницы. */
async function loadDonations(): Promise<void> {
    const box = document.getElementById('support-donations');
    const section = document.getElementById('support-donors-section');
    if (!box || !section) return;

    try {
        const res = await apiRequest<{ donations: Donation[] }>('/api/donations');
        const donations = res.donations ?? [];
        if (donations.length === 0) {
            section.hidden = true;
            return;
        }
        section.hidden = false;
        box.innerHTML = '';
        for (const d of donations) box.appendChild(buildDonationRow(d));
    } catch {
        // Недоступность списка не должна ломать страницу поддержки
        section.hidden = true;
    }
}

function formatAmount(d: Donation): string {
    const symbol = CURRENCY_SYMBOLS[d.currency] ?? d.currency;
    const value = Number(d.amount).toLocaleString(
        getLocale() === 'ru' ? 'ru-RU' : 'en-US',
        { maximumFractionDigits: 2 }
    );
    return `${value} ${symbol}`;
}

function formatDate(iso: string): string {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(getLocale() === 'ru' ? 'ru-RU' : 'en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

function buildDonationRow(d: Donation): HTMLElement {
    const row = document.createElement('div');
    row.className = 'support-donation';

    const head = document.createElement('div');
    head.className = 'support-donation-head';

    const name = document.createElement('span');
    name.className = 'support-donation-name';
    name.textContent = d.donor_name;

    const amount = document.createElement('span');
    amount.className = 'support-donation-amount';
    amount.textContent = formatAmount(d);

    head.append(name, amount);
    row.appendChild(head);

    if (d.message) {
        const msg = document.createElement('div');
        msg.className = 'support-donation-message';
        msg.textContent = d.message;
        row.appendChild(msg);
    }

    const date = document.createElement('div');
    date.className = 'support-donation-date';
    date.textContent = formatDate(d.donated_at);
    row.appendChild(date);

    return row;
}

export function unmountSupport(): void {
    /* нет ресурсов для разблокировки */
}
